import { Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  FileText,
  History,
  Lock,
  Share2,
  Sparkles,
  Star,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { SiteHeader } from '@/components/site-header'
import { SiteFooter } from '@/components/site-footer'
import { useCreateDocument } from '@/lib/documents-query'
import { useToast } from '@/hooks/use-toast'

export function LandingPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const create = useCreateDocument()

  const handleCreateDocument = async () => {
    try {
      const doc = await create.mutateAsync()
      navigate({ to: '/d/$id', params: { id: doc.id } })
    } catch {
      toast({
        title: 'Could not create document',
        description: 'Please try again in a moment.',
        variant: 'destructive',
      })
    }
  }

  const isCreating = create.isPending

  return (
    <div className="flex min-h-screen flex-col">
      {/* Header */}
      <SiteHeader />

      <main className="flex-1 flex flex-col items-center justify-center">
        {/* Hero Section */}
        <section className="relative overflow-hidden w-full flex flex-col items-center justify-center">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-purple-100/20 dark:to-purple-900/20 -z-10"></div>

          {/* Decorative elements */}
          <div className="absolute top-20 right-[10%] w-72 h-72 bg-primary/5 dark:bg-primary/10 rounded-full blur-3xl -z-10"></div>
          <div className="absolute bottom-10 left-[5%] w-96 h-96 bg-purple-200/20 dark:bg-purple-900/20 rounded-full blur-3xl -z-10"></div>

          <div className="container px-4 md:px-6 py-20 md:py-32 flex flex-col items-center justify-center">
            <div className="grid gap-6 lg:grid-cols-2 items-center justify-center">
              <div className="flex flex-col gap-4">
                <div className="inline-block">
                  <span className="inline-flex items-center rounded-full px-3 py-1 text-sm bg-primary/10 text-primary font-medium mb-2">
                    <Sparkles className="mr-1 h-3.5 w-3.5" />
                    Collaborative Markdown Editor
                  </span>
                </div>
                <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl lg:text-7xl">
                  Create, Edit, <span className="text-primary">Collaborate</span>
                </h1>
                <p className="max-w-[600px] text-muted-foreground md:text-xl">
                  A powerful markdown editor that lets you work together in real-time. No account required to get
                  started.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <Button
                    size="lg"
                    onClick={handleCreateDocument}
                    disabled={isCreating}
                    className="bg-primary hover:bg-primary/90"
                  >
                    {isCreating ? 'Creating...' : 'Start Writing Now'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link to="/register">Create Free Account</Link>
                  </Button>
                </div>
                <div className="flex items-center gap-4 mt-4">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center border-2 border-background"
                      >
                        <span className="text-xs font-medium">{String.fromCharCode(64 + i)}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Join <span className="font-medium">2,000+</span> users already creating documents
                  </p>
                </div>
              </div>
              <div className="relative lg:ml-10">
                <div className="relative rounded-lg border bg-background shadow-xl overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-12 bg-muted/50 flex items-center px-4 border-b">
                    <div className="flex space-x-2">
                      <div className="h-3 w-3 rounded-full bg-red-500"></div>
                      <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                      <div className="h-3 w-3 rounded-full bg-green-500"></div>
                    </div>
                  </div>
                  <div className="pt-12">
                    <img
                      src="/placeholder.svg"
                      alt="Editor Screenshot"
                      width={800}
                      height={600}
                      className="w-full object-cover block dark:hidden"
                      loading="eager"
                    />
                    <img
                      src="/placeholder-dark.svg"
                      alt="Editor Screenshot"
                      width={800}
                      height={600}
                      className="w-full object-cover hidden dark:block"
                      loading="eager"
                    />
                  </div>
                </div>
                {/* Floating badges */}
                <div className="absolute -top-4 -right-4 bg-white dark:bg-background rounded-lg shadow-lg p-3 flex items-center gap-2 border border-border">
                  <Users className="h-5 w-5 text-primary" />
                  <span className="font-medium">Real-time collaboration</span>
                </div>
                <div className="absolute -bottom-4 -left-4 bg-white dark:bg-background rounded-lg shadow-lg p-3 flex items-center gap-2 border border-border">
                  <Share2 className="h-5 w-5 text-green-500" />
                  <span className="font-medium">Easy sharing</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 md:py-24 bg-white dark:bg-background">
          <div className="container px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
                Powerful Features for Seamless Collaboration
              </h2>
              <p className="mt-4 text-xl text-muted-foreground max-w-3xl mx-auto">
                Everything you need to create, edit, and share markdown documents with your team
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {/* Feature 1 */}
              <div className="bg-muted/30 dark:bg-muted/60 rounded-lg p-6 border border-border hover:shadow-md transition-shadow">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-2">Rich Markdown Editing</h3>
                <p className="text-muted-foreground mb-4">
                  Full markdown support with syntax highlighting, live preview, and formatting toolbar.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Syntax highlighting</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Split view editing</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Formatting toolbar</span>
                  </li>
                </ul>
              </div>

              {/* Feature 2 */}
              <div className="bg-muted/30 dark:bg-muted/60 rounded-lg p-6 border border-border hover:shadow-md transition-shadow">
                <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-green-500" />
                </div>
                <h3 className="text-xl font-bold mb-2">Real-time Collaboration</h3>
                <p className="text-muted-foreground mb-4">
                  Work together with your team in real-time. See who&apos;s editing and what changes they&apos;re making.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Multiple editors at once</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Real-time updates</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>User presence indicators</span>
                  </li>
                </ul>
              </div>

              {/* Feature 3 */}
              <div className="bg-muted/30 dark:bg-muted/60 rounded-lg p-6 border border-border hover:shadow-md transition-shadow">
                <div className="h-12 w-12 rounded-full bg-blue-500/10 flex items-center justify-center mb-4">
                  <Share2 className="h-6 w-6 text-blue-500" />
                </div>
                <h3 className="text-xl font-bold mb-2">Flexible Sharing</h3>
                <p className="text-muted-foreground mb-4">
                  Share your documents with a simple link. Control who can view and edit your documents.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Read-only or editable links</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>No account required to view</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>One-click sharing</span>
                  </li>
                </ul>
              </div>

              {/* Feature 4 */}
              <div className="bg-muted/30 dark:bg-muted/60 rounded-lg p-6 border border-border hover:shadow-md transition-shadow">
                <div className="h-12 w-12 rounded-full bg-purple-500/10 flex items-center justify-center mb-4">
                  <Clock className="h-6 w-6 text-purple-500" />
                </div>
                <h3 className="text-xl font-bold mb-2">Work Anywhere</h3>
                <p className="text-muted-foreground mb-4">
                  Access your documents from any device, anytime. No installation required.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Cloud-based storage</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Mobile-friendly interface</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Works in any browser</span>
                  </li>
                </ul>
              </div>

              {/* Feature 5 */}
              <div className="bg-muted/30 dark:bg-muted/60 rounded-lg p-6 border border-border hover:shadow-md transition-shadow">
                <div className="h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center mb-4">
                  <History className="h-6 w-6 text-amber-500" />
                </div>
                <h3 className="text-xl font-bold mb-2">Document Management</h3>
                <p className="text-muted-foreground mb-4">
                  Organize and manage all your documents in one place with our intuitive dashboard.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Document dashboard</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Search and filter</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Document previews</span>
                  </li>
                </ul>
              </div>

              {/* Feature 6 */}
              <div className="bg-muted/30 dark:bg-muted/60 rounded-lg p-6 border border-border hover:shadow-md transition-shadow">
                <div className="h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
                  <Lock className="h-6 w-6 text-red-500" />
                </div>
                <h3 className="text-xl font-bold mb-2">Privacy & Security</h3>
                <p className="text-muted-foreground mb-4">
                  Your documents are private by default. You control who can access them.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Private by default</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Granular access control</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Secure sharing options</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-16 md:py-24 bg-muted/30 dark:bg-muted/60 w-full">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">How It Works</h2>
              <p className="mt-4 text-xl text-muted-foreground max-w-3xl mx-auto">
                Get started in seconds, no account required
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              {/* Step 1 */}
              <div className="flex flex-col items-center text-center">
                <div className="h-16 w-16 rounded-full bg-primary flex items-center justify-center text-white text-2xl font-bold mb-4">
                  1
                </div>
                <h3 className="text-xl font-bold mb-2">Create a Document</h3>
                <p className="text-muted-foreground">
                  Click &quot;Start Writing Now&quot; to create a new document instantly. No signup required.
                </p>
              </div>

              {/* Step 2 */}
              <div className="flex flex-col items-center text-center">
                <div className="h-16 w-16 rounded-full bg-primary flex items-center justify-center text-white text-2xl font-bold mb-4">
                  2
                </div>
                <h3 className="text-xl font-bold mb-2">Edit & Format</h3>
                <p className="text-muted-foreground">
                  Use our powerful editor to write and format your content with Markdown.
                </p>
              </div>

              {/* Step 3 */}
              <div className="flex flex-col items-center text-center">
                <div className="h-16 w-16 rounded-full bg-primary flex items-center justify-center text-white text-2xl font-bold mb-4">
                  3
                </div>
                <h3 className="text-xl font-bold mb-2">Share & Collaborate</h3>
                <p className="text-muted-foreground">Share your document with others and collaborate in real-time.</p>
              </div>
            </div>

            <div className="mt-12 text-center">
              <Button size="lg" onClick={handleCreateDocument} disabled={isCreating}>
                {isCreating ? 'Creating...' : 'Try It Now'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-16 md:py-24 bg-white dark:bg-background">
          <div className="container px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">What Our Users Say</h2>
              <p className="mt-4 text-xl text-muted-foreground max-w-3xl mx-auto">
                Join thousands of satisfied users who love mkdn.app
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {/* Testimonial 1 */}
              <div className="bg-muted/30 dark:bg-muted/60 rounded-lg p-6 border border-border">
                <div className="flex items-center mb-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-6 italic">
                  &quot;mkdn.app has transformed how our team collaborates on documentation. The real-time editing and
                  sharing features are game-changers for our workflow.&quot;
                </p>
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                    <span className="font-medium">JD</span>
                  </div>
                  <div>
                    <h4 className="font-semibold">Jane Doe</h4>
                    <p className="text-sm text-muted-foreground">Product Manager at TechCorp</p>
                  </div>
                </div>
              </div>

              {/* Testimonial 2 */}
              <div className="bg-muted/30 dark:bg-muted/60 rounded-lg p-6 border border-border">
                <div className="flex items-center mb-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-6 italic">
                  &quot;As a technical writer, I&apos;ve tried many markdown editors, but none compare to mkdn.app. The
                  interface is intuitive, and the collaboration features are seamless.&quot;
                </p>
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center mr-3">
                    <span className="font-medium">MS</span>
                  </div>
                  <div>
                    <h4 className="font-semibold">Michael Smith</h4>
                    <p className="text-sm text-muted-foreground">Technical Writer</p>
                  </div>
                </div>
              </div>

              {/* Testimonial 3 */}
              <div className="bg-muted/30 dark:bg-muted/60 rounded-lg p-6 border border-border">
                <div className="flex items-center mb-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-6 italic">
                  &quot;I love that I can start writing without creating an account. The ability to share documents with
                  different permission levels is exactly what I needed.&quot;
                </p>
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center mr-3">
                    <span className="font-medium">AJ</span>
                  </div>
                  <div>
                    <h4 className="font-semibold">Alex Johnson</h4>
                    <p className="text-sm text-muted-foreground">Freelance Developer</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-primary/5 dark:bg-primary/10 w-full relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-72 h-72 bg-primary/10 dark:bg-primary/20 rounded-full blur-3xl -z-10"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-200/20 dark:bg-purple-900/20 rounded-full blur-3xl -z-10"></div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl mb-6">Ready to Get Started?</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Join thousands of users who are already creating, editing, and sharing documents with mkdn.app.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={handleCreateDocument}
                disabled={isCreating}
                className="bg-primary hover:bg-primary/90"
              >
                {isCreating ? 'Creating...' : 'Create Document Now'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/register">Sign Up for Free</Link>
              </Button>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">No credit card required. Start for free.</p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <SiteFooter />
    </div>
  )
}
